<script lang="ts">
const __DsBlock = {
	type: 'input',
	category: 'input',
	hidden: true,
}
</script>
<script setup lang="ts">
import type { CompositePrimaryKey } from '#layers/data-model';
import type { RelatedDisplayItem } from '~/components/RelatedItemListCard.vue';
import type { RelationFormControlProps } from '~/composables/form-block';
import type { FormSaveContext, FormSaveError } from '~/composables/form-block/types';
import type { InputBlockProps } from '~/types/input-block';
import { computed, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOverlay, useStore } from '#imports';
import { canAssignRole, getKeyId } from '#layers/access';
import { toStructuredError } from '#layers/base/utils/error';
import { defineBlock } from '#layers/block-engine';
import { useCollectionByName } from '#layers/data-model';
import RelatedItemListCard from '~/components/RelatedItemListCard.vue';
import { injectFormBlockContext } from '~/composables/form-block/form-context';
import { useRelationFormField } from '~/composables/form-block/use-relation-form-field';
import { connectMemberRoles, disconnectMemberRoles } from '~/utils/api/member-roles';
import { buildAssignmentFilter } from './assignment-filter';
import { useAssignmentEntitlement } from './use-assignment-entitlement';
import { usePendingConnections } from './use-pending-connections';

interface MonospaceRole {
	id: string;
	name: string;
}

export interface InputRoleAssignmentBlockProps extends InputBlockProps, RelationFormControlProps {}

const props = defineProps<InputRoleAssignmentBlockProps>();

const LazyCollectionListingDrawer = defineAsyncComponent(() => import('~/components/Collection/CollectionListingDrawer.vue'));

;

const store = useStore();
const { t } = useI18n();

const formBlockContext = injectFormBlockContext();
const { collection: roleCollection } = useCollectionByName('MonospaceRole');
const { referencedItemKey: userKey } = useRelationFormField(props);

const { allowedAssignableIds: allowedRoleIds, canAssign, canDisconnectItem: canDisconnectRole, allowedDisconnections } = useAssignmentEntitlement(canAssignRole());

const { data: userRoles, refresh: refreshUserRoles } = store.MonospaceRole.query((q) => q.many({
	filter: {
		users: { _some: { userId: userKey.value!.id } },
	},
	// TODO(hannes) no-cache required here since we can't resolve relational filters locally
	fetchPolicy: 'no-cache',
}));

const currentRoleIds = computed(() => userRoles.value.map(({ id }) => id));

const {
	pendingConnections,
	pendingDisconnections,
	effectiveIds,
	hasChanges,
	connect,
	disconnect,
	undo,
	getItemState,
	clearConnections,
	clearDisconnections,
} = usePendingConnections(currentRoleIds);

// Load pending roles into the cache so the effective-id query can stay cache-only.
store.MonospaceRole.query((q) => q.many({ filter: { id: { _in: [...pendingConnections.value] } } }));
const { data: roleItems } = store.MonospaceRole.query((q) => q.many({ filter: { id: { _in: [...effectiveIds.value] } }, fetchPolicy: 'cache-only' }));

const displayItems = computed<RelatedDisplayItem<MonospaceRole>[]>(() =>
	roleItems.value.map((item) => ({
		id: item.id,
		data: item,
		state: getItemState(item.id),
	})),
);

async function handleSelectRoles() {
	if (!roleCollection.value) return;

	const modal = useOverlay().create(LazyCollectionListingDrawer, {
		props: {
			collectionId: roleCollection.value.id,
			selectionMode: 'multiple',
			filter: buildAssignmentFilter(allowedRoleIds.value, [...effectiveIds.value]),
		},
	});

	const result = await modal.open().result as { selection: CompositePrimaryKey[] } | undefined;

	if (result?.selection) {
		for (const key of result.selection) {
			connect(getKeyId(key)!);
		}
	}
}

function handleRemoveRole(item: MonospaceRole) {
	disconnect(item.id);
}

function handleUndoRole(item: MonospaceRole) {
	undo(item.id);
}

formBlockContext.onSave(async ({ itemKey }: FormSaveContext): Promise<FormSaveError[]> => {
	const errors: FormSaveError[] = [];
	const userId = getKeyId({ id: itemKey.id })!;

	if (pendingConnections.value.size > 0) {
		try {
			await connectMemberRoles(userId, [...pendingConnections.value]);
			clearConnections();
		}
		catch (error) {
			errors.push({ message: t('pages.settings.access.errors.attach_roles_failed'), source: toStructuredError(error) });
		}
	}

	// Re-check the allowlist at save time so a staged removal can never detach a role the user may not assign.
	const roleIdsToDisconnect = allowedDisconnections([...pendingDisconnections.value]);

	if (roleIdsToDisconnect.length > 0) {
		const junctionItems = store.MonospaceUserRole.peekMany({
			filter: { userId, roleId: { _in: roleIdsToDisconnect } },
		});

		try {
			await disconnectMemberRoles(userId, roleIdsToDisconnect);

			for (const item of junctionItems) {
				store.MonospaceUserRole.clearItem(store.MonospaceUserRole.getKey(item)!);
			}

			clearDisconnections();
		}
		catch (error) {
			errors.push({ message: t('pages.settings.access.errors.detach_roles_failed'), source: toStructuredError(error) });
		}
	}

	try {
		await refreshUserRoles();
	}
	catch {
		// Refresh failure is non-fatal - user can manually refresh
	}

	return errors;
});

formBlockContext.reportHasChanges(() => hasChanges.value);
</script>

<template>
	<RelatedItemListCard
		v-if="roleCollection"
		:items="displayItems"
		:collection-id="roleCollection.id"
		:disabled
		:disabled-notice
		:show-edit="false"
		:show-create="false"
		:permissions="{ canConnect: canAssign, canDisconnectItem: canDisconnectRole }"
		show-add-existing
		@select="handleSelectRoles"
		@remove="handleRemoveRole"
		@undo="handleUndoRole"
	/>
</template>
